import { TOPIC_CLASSIFICATION_SEEDS } from './topic-classification-seeds';
import { Topic } from '@api/topic/@shared/topic.entity';
import { Organization } from '@api/organization/@shared/organization.entity';
import { Person } from '@api/person/@shared/person.entity';
import { User } from '@api/user/@shared/user.entity';
import { Cluster } from '@api/cluster/@shared/cluster.entity';
import { Qualification } from '@lib/types';

type EntityKey = keyof Organization | keyof Person | keyof Topic | keyof User | keyof Cluster;
export type OptionsStructure = Record<EntityKey, ReadonlyArray<string>>;

// TODO: add all available form field options
export const FORM_FIELD_OPTIONS = {
  academicTitle: [
    'Abteilungsdirektor',
    'Amtsärztin',
    'Bundesminister',
    'Bundesministerin',
    'Diakon',
    'Dipl.-Psych.',
    'Direktorin',
    'Dr. Dr.',
    'Dr. h.c.',
    'Dr. med.',
    'Dr. phil.',
    'Dr. rer. biol. hum.',
    'Dr. rer. nat.',
    'Dr.',
    'Minister',
    'Ministerialrätin',
    'Ministerin',
    'PD Dr. med. habil.',
    'PD Dr. med.',
    'PD Dr.',
    'Parl. Staatssekretär',
    'Parl. Staatssekretärin',
    'Pastor',
    'Pfarrer',
    'Prof. Dr. Dr.',
    'Prof. Dr. habil.',
    'Prof. Dr. med.',
    'Prof. Dr. paed.',
    'Prof. Dr. phil.',
    'Prof. Dr. rer. nat.',
    'Prof. Dr.',
    'Prof. em. Dr. Dr.',
    'Prof.',
    'Regierungsdirektor',
    'Staatsministerin',
    'Staatssekretär Dr.',
    'Staatssekretär',
    'Staatssekretärin Dr.',
    'Staatssekretärin',
    'Vorsitzender',
  ],
  area: [
    'Bildung / Ausbildung (G)',
    'Erkrankungen und Behinderungen (G)',
    'Freizeit / Geselligkeit (G)',
    'Gebietsübergreifend (G)',
    'Gemeinwesen (G)',
    'Lebensformen / Zusammenleben (G)',
    'Lebenskrisen (G)',
    'Sucht / Abhängigkeit (G)',
    'Lebenslagen / Lebensführung (G)',
    'Politische Rechte (G)',
    'Umwelt (G)',
    'Ökonomie (G)',
  ],
  domain: TOPIC_CLASSIFICATION_SEEDS.domain,
  addressFederalState: [
    'Baden-Württemberg',
    'Bayern',
    'Berlin',
    'Brandenburg',
    'Bremen',
    'Hamburg',
    'Hessen',
    'Mecklenburg-Vorpommern',
    'Niedersachsen',
    'Nordrhein-Westfalen',
    'Rheinland-Pfalz',
    'Saarland',
    'Sachsen',
    'Sachsen-Anhalt',
    'Schleswig-Holstein',
    'Thüringen'
  ],
  icdArea: TOPIC_CLASSIFICATION_SEEDS.icdArea,
  icdGroup: TOPIC_CLASSIFICATION_SEEDS.icdGroup,
  qualifications: [
    'Autor*in',
    'Bibliothek',
    'DPWV',
    'Dienstleister',
    'Fachtreffen',
    'GAK Selbsthilfeförderung',
    'Journalist*in',
    'Krankenkasse gesetzlich Bund',
    'Krankenkasse gesetzlich Land',
    'Krankenkasse gesetzlich',
    'Körperschaft / Verband Ärzteschaft',
    'LAG',
    'Literaturbestellung',
    'Länderrat',
    'Ministerium Bund',
    'Ministerium Foerd. Land',
    'Ministerium Laender',
    'Mitarbeitende',
    'Mitglied DAG SHG',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'Privat',
    'Referierende',
    'Rentenversicherung Bund',
    'Rentenversicherung Land',
    'Rentenversicherung',
    'Tagungsstätte',
    'Vertreter*in SH §20h',
    'Vorstand DAG SHG',
    'Wissenschaftler*in',
    'Wohlfahrtsverband',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
  media: [
    'Newsletter',
    'Einladungen/Informationen',
    'Jahresbericht',
    'Publikationen',
  ],
  sector: [
    'Gesundheit',
    'Psychosoziales',
    'Sektorenübergreifend',
    'Soziales',
  ],
  status: [
    'Archiv',
    'Aufgenommen',
    'Aufnahmewunsch',
    'Datencheck ok',
    'Fundus',
    'Verzeichnis',
    'begrenzte Weitergabe',
    'ohne Rückmeldung',
  ],
  workingMethod: [
    'Selbsthilfekontaktstelle',
    'Selbsthilfe-Unterstützungsstelle',
  ],
  headquarters: [
    'Privat',
    'Öffentlich',
    'Keine',
  ],
  // Structured options used by forms with a fixed coverage-area select.
  // Red organization imports may still store arbitrary legacy text values.
  coverageArea: [
    'örtliche Gruppe/Community (Mitglieder nur aus einer Region)',
    'landesweite Gruppe/Community (Mitglieder aus einem Bundesland)',
    'bundesweite Gruppe/Community (Mitglieder aus zwei oder mehr Bundesländern)',
  ],
  targetGroup: [
    'Angehörige',
    'Betroffene',
    'Fachleute',
    'Interessierte',
  ],
  meetingFormat: [
    'Online',
    'Präsenz',
    'Hybrid (Präsenz mit Online-Anbindung)',
  ],
  language: [
    'Deutsch',
    'Englisch',
    'Gebärdensprache',
    'Andere',
  ],
  affectedPerson: [
    'Kind',
    'Erwachsene',
  ],
  interestedInfos: [
    'Krankheitsursachen und -verlauf',
    'Therapien, Operationen, Medikationen',
    'Forschungsergebnisse und wiss. Studien',
    'Sonstiges',
  ],
  experienceExchangeTopics: [
    'Psychische Belastungen',
    'Vereinbarkeit von Krankheit / Beeinträchtigung und Berufstätigkeit',
    'Sonstiges',
  ],
  foundingInterests: [
    '(feste) Selbsthilfegruppe',
    '(jederzeit offenes) Austauschnetzwerk',
    'Selbsthilfeorganisation für Patient/innen bzw. Gleichbetroffene',
    'Sonstiges',
  ],
  contactInterests: [
    'Erwachsenen Gleichbetroffenen',
    'Angehörigen',
    'Eltern gleichbetroffener Kinder',
    'Sonstiges',
  ],
  groupStatus: [
    'in Gründung',
    'offen für neue Mitglieder',
    'momentan geschlossen',
  ],
} as const satisfies Partial<OptionsStructure>;

export type QualificationSegment = 'type1' | 'type234' | 'type5' | 'type67' | 'type8';
const SEGMENTED_QUALIFICATIONS: Record<QualificationSegment, Qualification[]> = {
  type1: [
    'Fachtreffen',
    'GAK Selbsthilfeförderung',
    'Länderrat',
    'Mitglied DAG SHG',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
  type234: [
    'GAK Selbsthilfeförderung',
    'Mitglied DAG SHG',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
  type5: [
    'Mitglied DAG SHG',
    'GAK Selbsthilfeförderung',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
  type67: [
    'Bibliothek',
    'DPWV',
    'Dienstleister',
    'GAK Selbsthilfeförderung',
    'Krankenkasse gesetzlich Bund',
    'Krankenkasse gesetzlich Land',
    'Krankenkasse gesetzlich',
    'Körperschaft / Verband Ärzteschaft',
    'Literaturbestellung',
    'Ministerium Bund',
    'Ministerium Foerd. Land',
    'Ministerium Laender',
    'Mitglied DAG SHG',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'Rentenversicherung Bund',
    'Rentenversicherung Land',
    'Rentenversicherung',
    'Tagungsstätte',
    'Wohlfahrtsverband',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
  type8: [
    'Autor*in',
    'Fachtreffen',
    'GAK Selbsthilfeförderung',
    'Journalist*in',
    'LAG',
    'Länderrat',
    'Mitarbeitende',
    'Mitglied DAG SHG',
    'Patientenvertret. GBA - Bund - DAGSHG',
    'Patientenvertretung - Land - DAGSHG',
    'Patientenvertretung GBA - KoA',
    'Privat',
    'Referierende',
    'Vertreter*in SH §20h',
    'Vorstand DAG SHG',
    'Wissenschaftler*in',
    'VIP',
    'Junge Selbsthilfe',
    'Pflegeselbsthilfe',
  ],
};

type TSegmentedQualifications = typeof SEGMENTED_QUALIFICATIONS;
type QualificationListId = keyof TSegmentedQualifications;

export function getQualificationOptions(listId: QualificationListId): Qualification[] {
  return SEGMENTED_QUALIFICATIONS[listId];
}
